import React from 'react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { useCloseCurrentPanel } from '../../../panels-renderer/panel-context';
import type { PanelActionProps } from '../../panel-action/types';

import { PanelActionClose } from './panel-action-close';

export interface PanelActionCloseSmartProps extends Pick<PanelActionProps, 'testId' | 'label'> {
	/**
	 * Optional click handler for additional side effects (e.g., analytics).
	 * If provided, it will be called before closing the panel via context.
	 */
	onClick?: () => void;
}

/**
 * PanelActionCloseSmart is a "smart" version of PanelActionClose that automatically
 * closes the current panel using PanelContext. Must be used within a PanelsRenderer
 * that provides PanelContext.
 *
 * Use this when you want automatic panel closing behavior without managing state yourself.
 * For simple UI components without context, use PanelActionClose with an onClick handler instead.
 *
 * @example
 * ```tsx
 * // Automatic closing via context
 * <PanelActionCloseSmart />
 *
 * // With analytics
 * <PanelActionCloseSmart onClick={() => {
 *   trackEvent('panel-closed');
 * }} />
 * ```
 */
export function PanelActionCloseSmart({
	onClick,
	testId,
	label,
}: PanelActionCloseSmartProps): React.JSX.Element {
	const closePanel = useCloseCurrentPanel();

	const handleClick = (
		_event: React.MouseEvent<HTMLButtonElement>,
		_analyticsEvent?: UIAnalyticsEvent,
	) => {
		if (onClick) {
			onClick();
		}
		closePanel();
	};

	return <PanelActionClose onClick={handleClick} testId={testId} label={label} />;
}
